import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Empty, Input, Row, Col, Space, Progress, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { formatDate, formatMontant } from '../../variables/constants';
import { listerLots, STATUTS_LOT } from '../../services/attestationsService';
import TableExportButton from '../../components/TableExportButton';
import EditLotModal from './modals/EditLotModal';

import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

const { Search } = Input;

export default function LotsTab({ onCreate, refreshKey, actif }) {
  const { t } = useTranslation('attestations');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [editLot, setEditLot] = useState(null);

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res = await listerLots(page, 20);
      setData(res.items);
      setPagination(prev => ({ ...prev, current: res.currentPage + 1, pageSize: res.pageSize, total: res.totalElements }));
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load, refreshKey]);

  const filtered = data.filter(l =>
    !search || l.referenceLot?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    // Numero d'ordre dans la page affichee : repere de lecture pour retrouver une
    // ligne a l'oral ou dans un echange. Hors export, la numerotation n'a pas de
    // sens dans un fichier qu'on retrie.
    { title: t('commun.colonnes.ordre'), key: 'ordre', width: 56, align: 'center', fixed: 'left', exportable: false,
      render: (_, __, index) => (
        <span style={{ fontWeight: 700, color: '#94a3b8' }}>{index + 1}</span>
      ) },
    { title: t('commun.colonnes.reference'), dataIndex: 'referenceLot', key: 'ref', width: 150, fixed: 'left',
      render: (v) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>{v}</span> },
    { title: t('lots.colonnes.plageSerie'), key: 'plage', width: 200,
      export: (_, l) => `${l.numeroDebutSerie || ''} → ${l.numeroFinSerie || ''}`,
      render: (_, l) => (
        <span style={{ fontFamily: 'var(--font-main)', fontSize: 12 }}>
          {l.numeroDebutSerie} → {l.numeroFinSerie}
        </span>
      ) },
    { title: t('commun.colonnes.quantite'), dataIndex: 'quantite', key: 'qty', width: 110, align: 'right', numeric: true,
      render: (v) => <strong>{new Intl.NumberFormat(localeIntl()).format(v)}</strong> },
    { title: t('lots.colonnes.stock'), key: 'stock', width: 180, exportable: false,
      render: (_, l) => {
        const dist = l.quantiteDistribuee || 0;
        const pct = l.quantite ? Math.round((dist / l.quantite) * 100) : 0;
        return (
          <div>
            <Progress percent={pct} size="small" status={pct === 100 ? 'success' : 'active'}
              strokeColor="#1a6b3c" />
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {t('lots.distribuees', { dist, total: l.quantite })}
            </div>
          </div>
        );
      } },
    { title: t('lots.colonnes.prixAchat'), dataIndex: 'prixUnitaireAchat', key: 'pu', width: 110, align: 'right', format: 'currency', numeric: true,
      render: formatMontant },
    { title: t('commun.colonnes.dateCommande'), dataIndex: 'dateCommande', key: 'dc', width: 130, format: 'date', render: formatDate },
    { title: t('commun.colonnes.statut'), dataIndex: 'statutLot', key: 'st', width: 110,
      export: (v) => (STATUTS_LOT[v]?.label) || v || '',
      render: (v) => {
        const s = STATUTS_LOT[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      } },
    { title: '', key: 'act', width: 70, fixed: 'right', exportable: false,
      render: (_, l) => (
        <Tooltip title={t('lots.modifier')}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditLot(l)} />
        </Tooltip>
      ) },
  ];

  return (
    <>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Search placeholder={t('lots.recherche')} value={search}
            onChange={(e) => setSearch(e.target.value)} allowClear />
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => load(pagination.current - 1)}>{t('commun.actualiser')}</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {t('lots.nouveau')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* scroll.x doit rester >= a la somme des `width` ci-dessus (1116) et chaque
          colonne doit avoir une largeur. Sinon AntD passe la table en
          `table-layout: fixed` (colonnes figees) et ecrase les colonnes sans width
          a quelques pixels : leur en-tete deborde alors sur les voisines et fait
          gonfler toute la bande grise du thead, en-tetes centres verticalement. */}
      <Table className="cb-table" dataSource={filtered} columns={columns} rowKey="lotTrackingId"
        loading={loading} scroll={{ x: 1120 }}
        pagination={{
          current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
          onChange: (page) => load(page - 1),
          showTotal: (total) => t('lots.total', { total }),
        }}
        locale={{ emptyText: <Empty description={t('lots.vide')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />

      <EditLotModal
        open={!!editLot}
        lot={editLot}
        onClose={() => setEditLot(null)}
        onUpdated={() => { setEditLot(null); load(pagination.current - 1); }}
      />

      {/*  Export flottant  */}
      {/* RENDU SEULEMENT SI CET ONGLET EST ACTIF. Les quatre onglets de l'ecran
          portent chacun leur export ; AntD garde montes les volets deja visites, et
          quatre boutons flottants se seraient superposes au meme coin. La condition
          est ce qui garantit qu'il n'y en a qu'un.

          `floating` suffit au placement : le composant pose lui-meme l'enveloppe et
          la reserve de place en bas de page. */}
      {actif && (
        <TableExportButton
          data={filtered}
          columns={columns}
          filename="attestations-lots"
          title={t('lots.exportTitre')}
          subtitle={search ? t('commun.recherche', { search }) : undefined}
          floating
        />
      )}
    </>
  );
}
